import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import './index.scss';
import App from './App';
import { store, persistor } from './store';
import './i18n';

// Import zone test utilities in development
if (process.env.NODE_ENV === 'development') {
    import('./utils/zoneTestUtils');
}

// Get basename dynamically for development paths and PWA
const getBasename = () => {
  // Check for different deployment scenarios
  const currentPath = window.location.pathname;
  const isDevPath = currentPath.includes('/dev/');
  const isStagingPath = currentPath.includes('/staging/');
  const isPrototypePath = currentPath.match(/^\/v\d+-\d+-\d+-\d+/);
  
  const isNonProductionPath = isDevPath || isStagingPath || isPrototypePath;
  
  if (isNonProductionPath) {
    const pathParts = currentPath.split('/').filter(Boolean);
    
    console.log('🔍 Debug - currentPath:', currentPath);
    console.log('🔍 Debug - pathParts:', pathParts);
    console.log('🔍 Debug - isDevPath:', isDevPath);
    console.log('🔍 Debug - isStagingPath:', isStagingPath);
    console.log('🔍 Debug - isPrototypePath:', isPrototypePath);
    
    if (isDevPath) {
      // For original structure: www.artalex.me/lab/timecapsule/v1-09-08-25/
      // Find the 'dev' segment in the path and include everything up to the version
      const devIndex = pathParts.findIndex(part => part === 'dev');
      if (devIndex !== -1 && devIndex + 1 < pathParts.length) {
        // Include everything up to and including the version after 'dev'
        const basePath = '/' + pathParts.slice(0, devIndex + 2).join('/');
        console.log('🔧 React Router basename set for DEV:', basePath);
        return basePath;
      } else if (pathParts[0] === 'dev' && pathParts.length >= 2) {
        // Original logic for /dev/version/ structure
        const basePath = '/' + pathParts.slice(0, 2).join('/');
        console.log('🔧 React Router basename set for DEV (original):', basePath);
        return basePath;
      }
    } else if (isStagingPath && pathParts.length >= 1 && pathParts[0] === 'staging') {
      // Staging deployment: /staging/
      const basePath = '/' + pathParts[0];
      console.log('🔧 React Router basename set for STAGING:', basePath);
      return basePath;
    } else if (isPrototypePath && pathParts.length >= 1) {
      // Prototype deployment: /v1-11-06-25/
      const basePath = '/' + pathParts[0];
      console.log('🔧 React Router basename set for PROTOTYPE:', basePath);
      return basePath;
    }
    
    console.log('⚠️ Non-production path detected but parsing failed. Using empty basename.');
    return '';
  }
  
  // Fallback to PUBLIC_URL from environment
  const publicUrl = process.env.PUBLIC_URL || '';
  
  // If no PUBLIC_URL is set or it's just '/', return empty string
  if (!publicUrl || publicUrl === '/') return '';
  
  // Extract pathname from URL
  try {
    // For URLs like https://www.artalex.me/lab/timecapsule/v1-09-08-25/
    // This will extract '/lab/timecapsule/v1-09-08-25'
    const url = new URL(publicUrl);
    return url.pathname.replace(/\/$/, ''); // Remove trailing slash if present
  } catch (e) {
    // If it's not a valid URL, assume it's already a path
    // Ensure it starts with a slash and doesn't end with one
    return publicUrl.startsWith('/') 
      ? publicUrl.replace(/\/$/, '')
      : '/' + publicUrl.replace(/\/$/, '');
  }
};

console.log('🚀 Using HashRouter - no server configuration needed!');

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <HashRouter>
          <App />
        </HashRouter>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
